import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import App from './App';
import './index.css';
import ErrorBoundary from './components/ErrorBoundary';
import { ThemeProvider } from './context/ThemeContext';
import { withSuspense, withRetry } from './utils/routeUtils';
import './database/seedAuth';
import { initGA } from './utils/analytics';

if (!Object.hasOwn) {
  Object.defineProperty(Object, 'hasOwn', {
    value: (obj, key) => Object.prototype.hasOwnProperty.call(obj, key),
    writable: true,
    configurable: true,
  });
}

// Initialize Google Analytics
const gaId = import.meta.env.VITE_GA_MEASUREMENT_ID;

if (gaId) {
  initGA(gaId);
} else {
  console.warn('Google Analytics Measurement ID not found in environment variables');
}

const DEFAULT_THEME = 'light';
const THEME_CHOICE_KEY = 'site-theme-user-choice';
const savedTheme = localStorage.getItem('site-theme');
const hasExplicitThemeChoice = localStorage.getItem(THEME_CHOICE_KEY) === 'true';
const initialTheme =
  hasExplicitThemeChoice && (savedTheme === 'dark' || savedTheme === 'light')
    ? savedTheme
    : DEFAULT_THEME;

// Apply the default theme immediately to prevent a theme flash before React mounts.
if (initialTheme === 'dark') {
  document.documentElement.classList.add('dark');
  document.documentElement.classList.add('dark-theme');
  document.documentElement.classList.remove('light-theme');
} else {
  document.documentElement.classList.remove('dark');
  document.documentElement.classList.remove('dark-theme');
  document.documentElement.classList.add('light-theme');
}

if (!hasExplicitThemeChoice || savedTheme !== initialTheme) {
  localStorage.setItem('site-theme', initialTheme);
}

// Global error handler to catch initialization errors
window.addEventListener('error', (event) => {
  if (import.meta.env.DEV) {
    console.error('Global error caught:', event.error);
  }

  // Avoid surfacing browser extension errors as application UI in production.
  if (import.meta.env.DEV && !document.getElementById('error-display')) {
    const errorDisplay = document.createElement('div');
    errorDisplay.id = 'error-display';
    errorDisplay.style.position = 'fixed';
    errorDisplay.style.top = '0';
    errorDisplay.style.left = '0';
    errorDisplay.style.right = '0';
    errorDisplay.style.padding = '20px';
    errorDisplay.style.background = 'red';
    errorDisplay.style.color = 'white';
    errorDisplay.style.zIndex = '9999';
    errorDisplay.textContent = `Error: ${event.error?.message || 'Unknown error'}`;
    document.body.appendChild(errorDisplay);
  }
});

// This static marketing site should not keep old bundles alive through a service worker.
if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker
    .getRegistrations()
    .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())))
    .catch((error) => {
      if (import.meta.env.DEV) console.warn('SW unregister failed:', error);
    });
}

if (typeof window !== 'undefined' && 'caches' in window) {
  caches
    .keys()
    .then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
    .catch((error) => {
      if (import.meta.env.DEV) console.warn('Cache cleanup failed:', error);
    });
}

// Page components
const Home = withRetry(() => import('./pages/Home'));
const About = withRetry(() => import('./pages/About'));
const Contact = withRetry(() => import('./pages/Contact'));
const Blog = withRetry(() => import('./pages/Blog'));
const AdminLogin = withRetry(() => import('./pages/AdminLogin'));
const AdminDashboard = withRetry(() => import('./pages/AdminDashboard'));
const ServiceDetail = withRetry(() => import('./pages/ServiceDetail'));
const Services = withRetry(() => import('./pages/Services'));
const HealthCheck = withRetry(() => import('./pages/HealthCheck'));
const BlogDetail = withRetry(() => import('./pages/BlogDetail'));
const Privacy = withRetry(() => import('./pages/Privacy'));
const Terms = withRetry(() => import('./pages/Terms'));
const EigenLeads = withRetry(() => import('./pages/EigenLeads'));
const PATi = withRetry(() => import('./pages/PATi'));
const OFMS = withRetry(() => import('./pages/OFMS'));
const InDoc = withRetry(() => import('./pages/InDoc'));

// Custom service pages with unique designs
const AIAdvisory = withRetry(() => import('./pages/services/AIAdvisory'));
const DataAdvisory = withRetry(() => import('./pages/services/DataAdvisory'));
const AgenticAI = withRetry(() => import('./pages/services/AgenticAI'));

// Router configuration with future flags enabled
const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <App />,
      errorElement: <ErrorBoundary />,
      children: [
        { index: true, element: withSuspense(Home) },
        { path: 'about', element: withSuspense(About) },
        { path: 'contact', element: withSuspense(Contact) },
        { path: 'blog', element: withSuspense(Blog) },
        { path: 'blog/:identifier', element: withSuspense(BlogDetail) },
        { path: 'admin', element: withSuspense(AdminLogin) },
        { path: 'admin/*', element: withSuspense(AdminDashboard) },
        {
          path: 'services',
          children: [
            { index: true, element: withSuspense(Services) },
            { path: 'ai-advisory', element: withSuspense(AIAdvisory) },
            { path: 'data-advisory', element: withSuspense(DataAdvisory) },
            { path: 'agentic-ai-advisory', element: withSuspense(AgenticAI) },
            { path: ':slug', element: withSuspense(ServiceDetail) }
          ]
        },
        { path: 'health', element: withSuspense(HealthCheck) },
        { path: 'privacy', element: withSuspense(Privacy) },
        { path: 'terms', element: withSuspense(Terms) },
        { path: 'eigenleads', element: withSuspense(EigenLeads) },
        { path: 'pati', element: withSuspense(PATi) },
        { path: 'ofms', element: withSuspense(OFMS) },
        { path: 'indoc', element: withSuspense(InDoc) }
      ]
    }
  ],
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }
  }
);

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ThemeProvider>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </ThemeProvider>
  </React.StrictMode>
);
